import {
  BilikZoneFormUpdateQuery,
  BilikZoneSetInput,
  CityUpdates,
} from 'generated/graphql';
import { Formik, FormikHelpers } from 'formik';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import { ToastError, ToastSuccess } from 'utils/toast';

import { BilikZoneFormFields } from './bilik-zone-form-fields.type';
import BilikZoneFormView from './bilik-zone-form-view';
import { Loader } from 'semantic-ui-react';
import { MemberOrManager } from './bilik-zone-form-update-repository';
import { bilikZoneFormValidationSchema } from './bilik-zone-form-validation-schema';
import { useNavigate } from 'react-router';
import { PhoneNumber } from 'utils';

type BilikZoneFormUpdateProps = {
  updateBilikZone: (
    bilikZone: BilikZoneSetInput,
    removeManagerIds: number[],
    addManagers: MemberOrManager[],
    removeMemberIds: number[],
    addMembers: MemberOrManager[],
    updateCities: CityUpdates[],
  ) => Promise<void>;
  formData?: BilikZoneFormUpdateQuery['bilikZoneByPk'];
  bilikZoneId: number;
  loading: boolean;
};

const BilikZoneFormUpdateContainer: FunctionComponent<
  BilikZoneFormUpdateProps
> = ({ loading, formData, updateBilikZone, bilikZoneId }) => {
  const navigate = useNavigate();

  const startManagerIds =
    formData?.managers.map((manager) => manager.bilikPerson.id) || [];

  const startMemberIds =
    formData?.members.map((member) => member.bilikPerson.id) || [];

  const startCityParentIds = [
    ...(Array.from(
      new Set(
        formData?.cities
          .filter((city) => city.parentId)
          .map((city) => city.parentId) || [],
      ),
    ) as number[]), // Remove duplicates
  ];

  const startCitiesIds =
    formData?.cities
      .filter((city) => !startCityParentIds.includes(city.id))
      .map((city) => city.id) || [];

  const onSubmit = useCallback(
    async (
      values: BilikZoneFormFields,
      actions: FormikHelpers<BilikZoneFormFields>,
    ): Promise<void> => {
      try {
        // Diff initial managers and new values to find which managers need removing.
        const removeManagerIds: number[] = startManagerIds?.filter((item) => {
          return values?.managers.indexOf(item) < 0;
        });

        // Diff initial managers with new values to find which managers need to be added.
        const addManagers: MemberOrManager[] = values?.managers
          ?.filter((item) => startManagerIds.indexOf(item) < 0)
          .map((managerId) => {
            return { bilikPersonId: managerId, bilikZoneId };
          });

        // Diff initial members and new values to find which members need removing.
        const removeMemberIds: number[] = startMemberIds?.filter((item) => {
          return values?.members.indexOf(item) < 0;
        });

        // Diff initial members with new values to find which members need to be added.
        const addMembers: MemberOrManager[] = values?.members
          ?.filter((item) => startMemberIds.indexOf(item) < 0)
          .map((memberId) => {
            return { bilikPersonId: memberId, bilikZoneId };
          });

        const cityToAdd: CityUpdates[] = values?.cities.map((city) => {
          return {
            where: { id: { _eq: city.id } },
            _set: {
              bilikZoneId,
            },
          };
        });

        // Insert parent city if at least one district is selected (if Lyon 7 is selected, insert Lyon)
        values.cities
          .filter((city) => !!city.parentId)
          .map((city) => {
            cityToAdd.push({
              where: { id: { _eq: city.parentId ?? 0 } },
              _set: {
                bilikZoneId,
              },
            });
          });

        const citiesToRemove = startCitiesIds.filter((cityId) => {
          return !values.cities.find((city) => city.id === cityId);
        });

        // Delete parent city if no district is selected
        for (const initialParentId of startCityParentIds) {
          if (
            !values.cities.find((city) => city.parentId === initialParentId)
          ) {
            citiesToRemove.push(initialParentId);
          }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const cityUpdates: CityUpdates[] = [
          ...cityToAdd,
          ...citiesToRemove.map((cityId) => {
            return {
              where: { id: { _eq: cityId } },
              _set: {
                bilikZoneId: null,
              },
            };
          }),
        ];

        const defaultCallTrackingTarget = {
          ...values?.defaultCallTrackingTarget,
          telephone: values?.defaultCallTrackingTarget.telephone
            ? PhoneNumber.toE164(values?.defaultCallTrackingTarget.telephone)
            : undefined,
        };

        const callTrackingTarget = values.isTelephoneRelayActive
          ? {
              ...values?.callTrackingTarget,
              telephone: values?.callTrackingTarget.telephone
                ? PhoneNumber.toE164(values?.callTrackingTarget.telephone)
                : undefined,
            }
          : defaultCallTrackingTarget;

        await updateBilikZone(
          {
            id: bilikZoneId,
            name: values?.name,
            guideName: values?.guideName,
            mainCityCode: values?.mainCityCode,
            slug: values?.slug,
            genericEmail: values?.genericEmail,
            telephone: PhoneNumber.toE164(values?.telephone),
            addressLocality: values?.addressLocality,
            postOfficeBoxNumber: values?.postOfficeBoxNumber,
            streetAddress: values?.streetAddress,
            postalCode: values?.postalCode,
            regionPostalCode: values?.regionPostalCode,
            status: values?.status,
            zohoOrganizationId: values?.zohoOrganizationId,
            callTrackingTarget: callTrackingTarget,
            defaultCallTrackingTarget: defaultCallTrackingTarget,
            hasMultiplePostalCode: values?.hasMultiplePostalCode,
            hasDistrict: values?.hasDistrict,
            mainCityId: values?.mainCityId ?? 0,
            shouldDisplayContactInPiSms: values?.shouldDisplayContactInPiSms,
          },
          removeManagerIds,
          addManagers,
          removeMemberIds,
          addMembers,
          cityUpdates,
        );

        ToastSuccess('Succès', 'Zone enregistrée');
        actions.setSubmitting(false);
        navigate('/bilik_zones');
      } catch (err) {
        ToastError('Erreur', "Impossible d'enregistrer la zone");
      }
    },
    [updateBilikZone, navigate, bilikZoneId, startMemberIds],
  );

  const initialValues: BilikZoneFormFields = useMemo(() => {
    const isTelephoneRelayActive =
      formData?.callTrackingTarget?.telephone !==
      formData?.defaultCallTrackingTarget?.telephone;

    return {
      id: formData?.id || null,
      name: formData?.name || '',
      guideName: formData?.guideName || '',
      mainCityCode: formData?.mainCityCode || '',
      slug: formData?.slug || '',
      genericEmail: formData?.genericEmail || '',
      telephone: formData?.telephone || '',
      addressLocality: formData?.addressLocality || '',
      postOfficeBoxNumber: formData?.postOfficeBoxNumber || '',
      streetAddress: formData?.streetAddress || '',
      postalCode: formData?.postalCode || '',
      regionPostalCode: formData?.regionPostalCode || '',
      status: formData?.status || 'published',
      zohoOrganizationId: formData?.zohoOrganization?.id,
      managers:
        formData?.managers.map((manager) => manager.bilikPerson.id) || [], // ManagerIds
      members: formData?.members.map((member) => member.bilikPerson.id) || [], // MemberIds
      callTrackingTarget: formData?.callTrackingTarget || {
        name: '',
        telephone: '',
      },
      cities:
        formData?.cities.map((city) => ({
          id: city.id,
          type: 'include',
          parentId: city.parentId || null,
        })) || [],
      hasMultiplePostalCode: formData?.hasMultiplePostalCode || false,
      hasDistrict: formData?.hasDistrict || false,
      mainCityId: formData?.mainCityId || 0,
      shouldDisplayContactInPiSms:
        formData?.shouldDisplayContactInPiSms || false,
      defaultCallTrackingTarget: formData?.defaultCallTrackingTarget || {
        name: '',
        telephone: '',
      },
      isTelephoneRelayActive: isTelephoneRelayActive,
    };
  }, [loading]);

  if (loading) {
    return (
      <Loader size="big" active inline="centered" content="Chargement..." />
    );
  }

  return (
    <Formik
      validateOnChange={true}
      validationSchema={bilikZoneFormValidationSchema({
        currentSlug: formData?.slug,
      })}
      initialValues={initialValues}
      onSubmit={onSubmit}
    >
      <BilikZoneFormView action="update" />
    </Formik>
  );
};

export default BilikZoneFormUpdateContainer;
